import React, { useEffect, useState, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { oktaAuth } from '../../lib/okta-config'
import { AuthContext, GlobalContext } from '../../services/context'
import {
  getHighestRole,
  getAssignments,
  getUserProfile,
  patchUser
} from '../../services/apiHelper'
import Logo from '../../assets/images/GrowMore360_Logo.svg'

const AuthCallback = () => {
  const history = useHistory()
  const { authDispatch } = useContext(AuthContext)
  const { trackEvent } = useContext(GlobalContext)
  const [error, setError] = useState(null)

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Process the Okta redirect and exchange code for tokens
        await oktaAuth.handleLoginRedirect()

        // Get the tokens from the token manager
        const accessToken = await oktaAuth.tokenManager.get('accessToken')
        const idToken = await oktaAuth.tokenManager.get('idToken')

        if (!accessToken || !idToken) {
          setError('Could not retrieve authentication tokens.')
          return
        }

        // Store accessToken in sessionStorage for the existing axios interceptor
        window.sessionStorage.setItem('accessToken', accessToken.accessToken)

        // Get user info from Okta
        const userData = await oktaAuth.getUser()

        // Dispatch login success to match existing auth flow
        trackEvent('successful_login', { response: 'okta login success' })
        authDispatch({ type: 'LOGIN_USER', payload: userData })

        const oktaId = accessToken.claims.uid

        // Try to get user profile by okta_id
        let userProfile = await getUserProfile(oktaId, 'okta_id').catch(() => null)

        // First access: okta_id not set yet — look up by email and set it
        if (!userProfile && userData.email) {
          const profileByEmail = await getUserProfile(userData.email, 'email').catch(() => null)
          if (profileByEmail) {
            await patchUser({ user_id: profileByEmail.user_id, okta_id: oktaId })
              .catch(() => null)
            userProfile = { ...profileByEmail, okta_id: oktaId }
          }
        }

        if (userProfile) {
          authDispatch({ type: 'UPDATE_USER_PROFILE', payload: userProfile })

          // Get assignments
          getAssignments(userProfile.user_id, 'user_id')
            .then(res => {
              if (res) {
                const assignmentsObj = {}
                res.forEach(company => {
                  assignmentsObj[company.company_id] = company.permissions
                })
                authDispatch({ type: 'UPDATE_USER_PERMISSIONS', payload: assignmentsObj })
              } else {
                authDispatch({ type: 'UPDATE_USER_PERMISSIONS_FAILED' })
              }
            })
        }

        // Get highest role
        const highestRole = await getHighestRole().catch(() => null)
        if (highestRole) {
          authDispatch({ type: 'SET_HIGHEST_ROLE', payload: highestRole.role_type })
          if (!userProfile) {
            authDispatch({ type: 'UPDATE_USER_PROFILE', payload: highestRole })
          }
        }

        // Redirect to dashboard
        history.replace('/')
      } catch (err) {
        console.error('Error during authentication callback:', err)
        trackEvent('failed_login', { response: 'okta callback failed' })

        // Check if user is not assigned to the Okta app
        if (err && err.name === 'OAuthError' && err.errorCode === 'access_denied') {
          setError('Access denied. Your account is not assigned to this application. Please contact support.')
          return
        }

        setError('Authentication failed. Please try again.')
      }
    }

    handleCallback()
  }, [history, authDispatch, trackEvent])

  if (error) {
    return (
      <div className='df fxdrc jcc aic' style={{ minHeight: '100vh' }}>
        <div
          className='bgc-white bd1-s-darkgray w375 df fxdrc aic'
          style={{ padding: '40px 25px', borderRadius: '8px', textAlign: 'center' }}
        >
          <img src={Logo} alt='Logo' style={{ width: '200px', marginBottom: '30px' }} />
          <h2 style={{ color: '#d32f2f', fontSize: '20px', marginBottom: '16px' }}>
            Authentication Failed
          </h2>
          <p style={{ color: '#555', fontSize: '14px', marginBottom: '24px' }}>
            {error}
          </p>
          <p style={{ color: '#888', fontSize: '12px', marginBottom: '16px' }}>
            Support: support@syngentadigital.com
          </p>
          <button
            onClick={() => history.replace('/login')}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#14803C',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='df fxdrc jcc aic' style={{ minHeight: '100vh' }}>
      <img src={Logo} alt='Logo' style={{ width: '200px', marginBottom: '30px' }} />
      <p style={{ color: '#555', fontSize: '18px' }}>Authenticating...</p>
    </div>
  )
}

export default AuthCallback
